import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AdminShell from "../components/admin/AdminShell";
import { apiFetch, useAuth } from "../hooks/useAuth";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const day = String(d.getUTCDate()).padStart(2, "0");
  const mon = months[d.getUTCMonth()];
  const yr = d.getUTCFullYear();
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return { date: `${day} ${mon} ${yr}`, time: `${hh}:${mm} UTC` };
};

const StatusPill = ({ status }) => {
  const isNew = status === "new";
  return (
    <span
      data-testid={`status-pill-${status}`}
      className={`inline-block px-2.5 py-1 border font-sans uppercase ${
        isNew ? "border-gold text-gold" : "border-navy text-navy"
      }`}
      style={{ fontSize: "9.5px", letterSpacing: "0.22em", borderRadius: 2 }}
    >
      {isNew ? "New" : "Reviewed"}
    </span>
  );
};

const FilterPill = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="group relative font-sans uppercase text-navy/85 hover:text-navy transition-colors"
    style={{ fontSize: "11px", letterSpacing: "0.22em" }}
  >
    <span className="relative">
      {children}
      <span
        aria-hidden="true"
        className={`absolute left-0 right-0 -bottom-1 h-px bg-gold transition-transform duration-300 ease-out origin-left ${
          active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
        }`}
      />
    </span>
  </button>
);

const AdminSubmissions = () => {
  const nav = useNavigate();
  const { loading, user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/admin/login");
  }, [loading, user, nav]);

  useEffect(() => {
    if (!user) return;
    setItems(null);
    apiFetch(`/api/admin/submissions?status=${filter}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => setItems(data))
      .catch((e) => {
        if (e === 401) nav("/admin/login");
        else setError("Unable to load.");
      });
  }, [filter, user, nav]);

  if (loading || !user) return null;

  return (
    <AdminShell user={user}>
      <span className="font-sans uppercase text-gold" style={{ fontSize: "11px", letterSpacing: "0.22em" }}>
        Office Console
      </span>
      <h1
        className="mt-6 font-serif font-normal text-navy"
        style={{ fontSize: "clamp(36px, 4vw, 56px)", lineHeight: 1.08, letterSpacing: "-0.01em" }}
      >
        Inquiries
      </h1>
      <p className="mt-3 font-serif italic text-navy/60" style={{ fontSize: "17px" }}>
        Listed in order of receipt. The most recent appears first.
      </p>
      <span aria-hidden="true" className="mt-8 block h-px w-8 bg-gold" />

      <nav className="mt-10 flex items-center gap-8" aria-label="Filter">
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")} testId="filter-all">All</FilterPill>
        <FilterPill active={filter === "new"} onClick={() => setFilter("new")} testId="filter-new">New</FilterPill>
        <FilterPill active={filter === "reviewed"} onClick={() => setFilter("reviewed")} testId="filter-reviewed">Reviewed</FilterPill>
      </nav>

      <div className="mt-10 border-t border-navy/8" data-testid="submissions-list">
        {error ? (
          <p className="py-16 text-center font-sans text-navy/60" style={{ fontSize: "14px" }}>{error}</p>
        ) : items === null ? (
          <p className="py-16 text-center font-sans text-navy/55" style={{ fontSize: "13px" }}>Loading…</p>
        ) : items.length === 0 ? (
          <div className="py-20 text-center">
            <span className="font-sans uppercase text-navy/55" style={{ fontSize: "11px", letterSpacing: "0.22em" }}>No inquiries</span>
            <p className="mt-4 font-serif italic text-navy/60" style={{ fontSize: "17px" }}>
              No submissions yet. New inquiries will appear here.
            </p>
          </div>
        ) : (
          items.map((it) => {
            const dt = fmtDate(it.submitted_at);
            const excerpt = (it.message || "").slice(0, 110) + (it.message && it.message.length > 110 ? "…" : "");
            return (
              <Link
                key={it.id}
                to={`/admin/submissions/${it.id}`}
                data-testid={`submission-row-${it.id}`}
                className="group grid grid-cols-12 gap-4 lg:gap-8 py-7 border-b border-navy/8 hover:border-navy/15 transition-colors"
              >
                <div className="col-span-12 md:col-span-3 flex flex-col gap-2">
                  <StatusPill status={it.status} />
                  <p className="mt-2 font-sans text-navy" style={{ fontSize: "13px", letterSpacing: "0.02em" }}>{dt.date}</p>
                  <p className="font-sans text-navy/55" style={{ fontSize: "11px" }}>{dt.time}</p>
                </div>
                <div className="col-span-12 md:col-span-7">
                  <h3 className="font-serif font-normal text-navy" style={{ fontSize: "clamp(20px, 2vw, 26px)", lineHeight: 1.15 }}>
                    {it.name}
                  </h3>
                  <p className="mt-1.5 font-sans uppercase text-navy/55" style={{ fontSize: "11px", letterSpacing: "0.22em" }}>
                    {it.company}
                  </p>
                  <p className="mt-3 font-sans text-navy/70" style={{ fontSize: "14px", lineHeight: 1.6 }}>{excerpt}</p>
                </div>
                <div className="col-span-12 md:col-span-2 flex md:justify-end items-start">
                  <span className="inline-flex items-center gap-2 font-sans uppercase text-navy/85 group-hover:text-gold transition-colors" style={{ fontSize: "11px", letterSpacing: "0.22em" }}>
                    <span>View</span>
                    <span aria-hidden="true" className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                  </span>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </AdminShell>
  );
};

export default AdminSubmissions;
